import { useMemo, useContext } from "react"
import { pick } from "lodash"
import { useQuery } from "@tanstack/react-query"
import { useDispatch } from "react-redux"
import { Creators } from "model/apiWrapper"
import { useAccountAlias } from "resources/account"
import { useClassifications } from "resources/classifications"
import { useContentSetsMetadata } from "resources/content-sets"
import { useModelMetadata } from "resources/models"
import { useModelCurrencies } from "resources/currencies"
import { useModelCountries } from "resources/countries"
import { cleanPositionSet } from "../query-builder"
import { escapeContentSetId } from "helpers/contentSet"
import { OK, combineTanStackStatuses } from "helpers/data-statuses"
import { DashboardContext } from "connected-components/dashboard/context/dashboard"
import type { FactorRule } from "components/search-factor/types"
import type { Country } from "resources/countries"
import { ContentSetWithEscapedId, SimulationData, ContentSetRaw, FactorRuleDisplayType, PositionSetInput, FactorRuleDisplay, ModelMetadata, FilterOption, Classification } from "../types"
import { FACTOR_RULE_DISPLAY_TYPES } from "../constants"
import { buildRulesQuery, validateQueryParams, SIMULATION_ALIASES } from "../query-builder"
import {
  transformFactorExposureToRules,
  transformContentSetFactorsToRules,
  transformBetasToRules,
  filterExistingRules,
  transformCompositionToRules,
  createRuleKey,
} from "../helpers"

interface GraphQLModelResponse {
  data: {
    model: {
      [SIMULATION_ALIASES.MAIN]: SimulationData | null
      [SIMULATION_ALIASES.ACTIVE]?: SimulationData | null
    } | null
  }
}

export type UseRulesResult = [FactorRule[], boolean, Error | null]

function indexFactorsById<T extends { id: string }>(items: T[] | undefined): Record<string, T> {
  if (!items) return {}
  return Object.fromEntries(items.map(item => [item.id, item]))
}

function getExposureRules(
  rulesData: SimulationData,
  factorsMetadata: Record<string, NonNullable<ModelMetadata["factors"]>[number]>,
  contentSets: ContentSetWithEscapedId[] | null,
): FactorRule[] {
  return [
    ...transformFactorExposureToRules(rulesData.exposure, factorsMetadata),
    ...transformContentSetFactorsToRules(rulesData, contentSets),
  ]
}

function getCompositionRules(
  rulesData: SimulationData,
  metadata: {
    countries: Country[] | null
    currencies: FilterOption[] | undefined
    classifications: Classification[] | null
  },
): FactorRule[] {
  return [
    ...transformCompositionToRules(rulesData.composition, metadata),
    ...transformBetasToRules(rulesData.beta),
  ]
}

export function useRules(
  positionSet: PositionSetInput,
  basePositionSet: PositionSetInput | null | undefined,
  ruleDisplayType: FactorRuleDisplayType,
  existingRules: FactorRuleDisplay[] = [],
): UseRulesResult {
  const { modelId, endDate } = useContext(DashboardContext)
  const dispatch = useDispatch()
  const account = useAccountAlias()

  const [contentSetsRaw, contentSetsStatus] = useContentSetsMetadata()
  const [classifications, classificationsStatus] = useClassifications()
  const [countries, countriesStatus] = useModelCountries(modelId)
  const [currencies, currenciesStatus] = useModelCurrencies(modelId)
  const [modelMetadata] = useModelMetadata(modelId)

  const contentSets = useMemo<ContentSetWithEscapedId[] | null>(() => {
    if (!contentSetsRaw) return null

    return contentSetsRaw.map((contentSet: ContentSetRaw): ContentSetWithEscapedId => ({
      ...contentSet,
      escapedId: escapeContentSetId(contentSet.id),
    }))
  }, [contentSetsRaw])

  const status = useMemo(() => combineTanStackStatuses([contentSetsStatus, classificationsStatus, countriesStatus, currenciesStatus]), [contentSetsStatus, classificationsStatus, countriesStatus, currenciesStatus])

  const query = useMemo(() => {
    if (status !== OK) {
      return null
    }

    if (!validateQueryParams(modelId, endDate, positionSet)) {
      return null
    }

    return buildRulesQuery(
      modelId,
      endDate,
      positionSet,
      contentSets,
      classifications,
      modelMetadata ?? undefined,
      basePositionSet,
    )
  }, [
    modelId,
    endDate,
    positionSet,
    basePositionSet,
    contentSets,
    classifications,
    modelMetadata,
    status,
  ])

  const positionSetKey = useMemo(() => positionSet ? JSON.stringify(cleanPositionSet(positionSet)) : null, [positionSet])
  const basePositionSetKey = useMemo(() => basePositionSet ? JSON.stringify(cleanPositionSet(basePositionSet)) : null, [basePositionSet])

  const result = useQuery({
    queryKey: [
      "optimize-rules",
      modelId,
      endDate,
      positionSetKey,
      basePositionSetKey,
    ],
    queryFn: () => new Promise<GraphQLModelResponse>((resolve, reject) => {
      dispatch(Creators.graphql(account, query?.query, query?.variables, resolve, reject))
    }),
    enabled: Boolean(query && modelId && endDate && positionSet),
    select: (value) => pick(value?.data?.model, Object.values(SIMULATION_ALIASES)),
  })

  const rulesData = result.data || null
  const isLoading = result.isLoading
  const isQueryReady = Boolean(query && !isLoading)

  const allRules: FactorRule[] = useMemo(() => {
    const mainSimulation = rulesData?.[SIMULATION_ALIASES.MAIN]
    if (!mainSimulation || !modelMetadata) return []

    const factorsMetadata = indexFactorsById(modelMetadata?.factors)

    const getRulesForSimulation = (simulationData: SimulationData) =>
      ruleDisplayType === FACTOR_RULE_DISPLAY_TYPES.EXPOSURE
        ? getExposureRules(simulationData, factorsMetadata, contentSets)
        : getCompositionRules(simulationData, { countries, currencies, classifications })

    const mainRules = getRulesForSimulation(mainSimulation)

    // If active simulation exists, enrich with activeValue
    const activeSimulation = rulesData?.[SIMULATION_ALIASES.ACTIVE]
    if (activeSimulation) {
      const activeRules = getRulesForSimulation(activeSimulation)
      // Create lookup map for access to active values by composite rule key
      const activeValuesByCompositeKey = Object.fromEntries(
        activeRules.map(rule => [createRuleKey(rule), rule.value]),
      )
      return filterExistingRules(
        mainRules.map(rule => ({
          ...rule,
          activeValue: activeValuesByCompositeKey[createRuleKey(rule)],
        })),
        existingRules,
      )
    }

    return filterExistingRules(mainRules, existingRules)
  }, [
    rulesData,
    modelMetadata,
    contentSets,
    classifications,
    existingRules,
    ruleDisplayType,
    countries,
    currencies,
  ])

  return [allRules, isQueryReady, result.error ?? null]
}
